import {
  handleSubmitMultilingualForm,
  useCreateAboutPage,
  useGetAboutPageFormData,
  useUpdateAboutPage,
} from "@/api/cms.ts";
import {
  AboutUsCreditForm,
  AboutUsForm,
  AboutUsFormProps,
  AboutUsLegalNoticesForm,
} from "@/components/cms/AboutUsForm";
import { Switch } from "@/components/form-items/AbstractForm";
import { DoubleMultilingualField } from "@/components/form-items/MultilingualField";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs.tsx";
import { useAuth } from "@/context/auth/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  constructLocaleDict,
  getMainLanguage,
  multilingualFieldSchema,
  SupportedLocale,
} from "@/i18n";
import CMSLayout from "@/layout/CMSLayout.tsx";
import { aboutUsContentSchema, cmsPageSchema } from "@/lib/cms/cms-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const itemFormSchema = cmsPageSchema
  .pick({ pageName: true, published: true })
  .merge(
    aboutUsContentSchema.omit({
      id: true,
    }),
  )
  .optional();

const formSchema = multilingualFieldSchema(itemFormSchema);

type FormData = z.infer<typeof formSchema>;

const AboutUsCMS = () => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { instanceData } = useAuth();

  const create = useCreateAboutPage();
  const update = useUpdateAboutPage();

  const { data: aboutData } = useGetAboutPageFormData();

  const defaultValues: FormData = useMemo(
    () =>
      constructLocaleDict((lang) => {
        const about = aboutData?.[lang];
        return {
          ...about?.content,
          publishedCredits: about?.content?.publishedCredits ?? false,
          publishedLegalNotices: about?.content?.publishedLegalNotices ?? false,
          pageName: about?.pageName ?? "",
          published: about?.published ?? false,
        };
      }),
    [aboutData],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    if (aboutData) {
      form.reset(defaultValues);
    }
  }, [defaultValues, aboutData, form]);

  const onSubmit = (submittedData: FormData) => {
    if (instanceData?.CMS?.id == null) {
      console.error("Tried to submit but there is no CMS");
      return;
    }

    Object.keys(form.formState.dirtyFields).forEach((key) => {
      const localeData = submittedData[key as SupportedLocale];
      const baseData = aboutData?.[key as SupportedLocale];
      if (!localeData) return;

      const mutation = baseData?.content.id ? update : create;

      const { pageName, published, ...content } = localeData;

      mutation.mutate(
        {
          id: baseData?.content.id ?? "",
          lang: key as SupportedLocale,
          data: {
            pageName,
            published,
            content,
          },
        },
        {
          onSuccess: () => {
            toast({
              title: t("success"),
              description: t("homepageSuccessMessage"),
              duration: 1000,
              variant: "success",
            });
          },
          onError: () => {
            toast({
              title: t("error"),
              description: t("homepageErrorMessage"),
              variant: "error",
              duration: 1000,
            });
          },
        },
      );
    });
  };
  const tabList = useMemo(
    () =>
      [
        {
          label:
            aboutData?.[getMainLanguage()]?.pageName ??
            t("cms.about-us.tabs.about-us"),
          component: AboutUsForm,
          value: "about-us",
          publishedSwitch: "published",
        },
        {
          label:
            aboutData?.[getMainLanguage()]?.content.creditsTitle ??
            t("cms.about-us.tabs.credits"),
          component: AboutUsCreditForm,
          value: "credits",
          publishedSwitch: "publishedCredits",
        },
        {
          label:
            aboutData?.[getMainLanguage()]?.content.legalNoticesTitle ??
            t("cms.about-us.tabs.legal-notices"),
          component: AboutUsLegalNoticesForm,
          value: "legal-notices",
          publishedSwitch: "publishedLegalNotices",
        },
      ] satisfies {
        label: string;
        value: string;
        publishedSwitch: string;
        component: (props: AboutUsFormProps) => JSX.Element;
      }[],
    [t, aboutData],
  );

  return (
    <CMSLayout pageName={t("cmsTabAPropos")} formIds={["about-us"]}>
      <FormProvider {...form}>
        <Tabs className="w-full" defaultValue="about-us" asChild>
          <form
            onSubmit={handleSubmitMultilingualForm(form, aboutData, onSubmit)}
            id="about-us"
            className="flex flex-col gap-4"
          >
            <TabsList>
              {tabList.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {tabList.map((tab) => {
              const Comp = tab.component;
              return (
                <TabsContent
                  key={tab.value}
                  value={tab.value}
                  className="inline-flex gap-8 w-full data-[state=inactive]:hidden"
                >
                  <DoubleMultilingualField
                    corner={(lang) => (
                      <Switch
                        dispLabel={false}
                        name={`${lang}.${tab.publishedSwitch}`}
                      />
                    )}
                    render={(lang) => <Comp language={lang} />}
                  />
                </TabsContent>
              );
            })}
          </form>
        </Tabs>
      </FormProvider>
    </CMSLayout>
  );
};

export default AboutUsCMS;
